import React, { useState, useEffect, useRef } from 'react';
import { Clock, RefreshCw, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PendingDebitsMonitor() {
  const [pendingOrders, setPendingOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState({});
  const [unauthorized, setUnauthorized] = useState(false);
  const intervalRef = useRef(null);

  const fetchPendingOrders = async () => {
    const token = localStorage.getItem('token');
    // Si no hay token, no spamear logs del servidor con 401.
    if (!token) {
      setUnauthorized(true);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    setLoading(true);
    try {
      // Buscar órdenes con débito pendiente
      const response = await axios.get(
        `${BACKEND_URL}/api/super-admin/all-orders?status_filter=pending`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Filtrar solo las que tienen debit_pending
      const ordersWithPending = (response.data || []).filter(
        order => order.debit_pending && order.debit_pending.status === 'pending_bcv'
      );
      
      setPendingOrders(ordersWithPending);
      setUnauthorized(false);
    } catch (error) {
      console.error('Error fetching pending debits:', error);
      // Si no autorizado, detener el polling para no inundar logs del backend.
      const status = error.response?.status;
      if (status === 401 || status === 403) {
        setUnauthorized(true);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingOrders();
    
    // Auto-refresh cada 30 segundos (se detiene si recibimos 401/403).
    intervalRef.current = setInterval(fetchPendingOrders, 30000);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  const checkDebitStatus = async (orderId) => {
    setChecking(prev => ({ ...prev, [orderId]: true }));
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/super-admin/debito-inmediato/consultar?order_id=${orderId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const result = response.data;
      
      if (result.success) {
        alert(`✅ Transacción completada!\nEndToEnd: ${result.endtoend}\nOrden marcada como pagada.`);
        fetchPendingOrders(); // Refrescar lista
      } else if (result.codigo === '9999') {
        alert(`⏳ Transacción aún pendiente de liquidación BCV.\n\nEl sistema seguirá verificando automáticamente cada 2 minutos.`);
      } else {
        alert(`❌ Error: ${result.mensaje}\nCódigo: ${result.codigo}`);
        fetchPendingOrders();
      }
    } catch (error) {
      alert(`Error al consultar: ${error.response?.data?.detail || error.message}`);
    } finally {
      setChecking(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleString('es-VE');
    } catch {
      return dateStr;
    }
  };

  const getTimeSince = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const diff = Date.now() - new Date(dateStr).getTime();
      const minutes = Math.floor(diff / 60000);
      if (minutes < 1) return 'Hace menos de 1 min';
      if (minutes < 60) return `Hace ${minutes} min`;
      const hours = Math.floor(minutes / 60);
      return `Hace ${hours}h ${minutes % 60}min`;
    } catch {
      return 'N/A';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <Clock className="h-6 w-6 mr-2 text-yellow-500" />
            Débitos Inmediatos Pendientes
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Transferencias interbancarias en proceso de liquidación BCV
          </p>
        </div>
        <button
          onClick={fetchPendingOrders}
          disabled={loading}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </div>

      {/* Info Box */}
      <div className="mb-6 bg-blue-50 border-l-4 border-blue-400 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <AlertCircle className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              <strong>Sistema Automático Activo:</strong> El sistema verifica automáticamente cada 2 minutos el estado de estas transacciones. 
              Puedes usar el botón "Verificar Ahora" para consultar manualmente en cualquier momento.
            </p>
          </div>
        </div>
      </div>

      {/* Lista de órdenes pendientes */}
      {unauthorized ? (
        <div className="text-center py-12" data-testid="pending-debits-unauthorized">
          <AlertCircle className="mx-auto h-12 w-12 text-orange-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Sesión no válida</h3>
          <p className="mt-1 text-sm text-gray-500">
            Inicia sesión como Super Admin para ver los débitos pendientes.
          </p>
        </div>
      ) : loading && pendingOrders.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Cargando...</span>
        </div>
      ) : pendingOrders.length === 0 ? (
        <div className="text-center py-12">
          <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Sin débitos pendientes</h3>
          <p className="mt-1 text-sm text-gray-500">
            Todas las transacciones interbancarias han sido procesadas
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {pendingOrders.map((order) => (
            <div
              key={order.order_id}
              className="border border-yellow-200 bg-yellow-50 rounded-lg p-4"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Orden #{order.order_code}
                    </h3>
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                      Pendiente BCV
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Monto</p>
                      <p className="font-semibold text-gray-900">
                        {order.currency} {order.amount.toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Cliente</p>
                      <p className="font-medium text-gray-900">{order.payer_name}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">EndToEnd</p>
                      <p className="font-mono text-xs text-gray-700">
                        {order.debit_pending?.endtoend?.slice(0, 20) || 'N/A'}...
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Tiempo Transcurrido</p>
                      <p className="font-medium text-gray-900">
                        {getTimeSince(order.debit_token_requested_at)}
                      </p>
                    </div>
                  </div>

                  {order.debit_pending?.last_checked && (
                    <div className="mt-2 text-xs text-gray-500">
                      Última verificación: {formatDate(order.debit_pending.last_checked)} 
                      {' '}(Intentos: {order.debit_pending.check_count || 0})
                    </div>
                  )}
                </div>

                <button
                  onClick={() => checkDebitStatus(order.order_id)}
                  disabled={checking[order.order_id]}
                  className="ml-4 flex items-center px-3 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  <RefreshCw className={`h-4 w-4 mr-1 ${checking[order.order_id] ? 'animate-spin' : ''}`} />
                  Verificar Ahora
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Stats */}
      {pendingOrders.length > 0 && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-yellow-600">{pendingOrders.length}</p>
              <p className="text-sm text-gray-500">Pendientes</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-600">
                VES {pendingOrders.reduce((sum, o) => sum + o.amount, 0).toFixed(2)}
              </p>
              <p className="text-sm text-gray-500">Monto Total</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-600">2 min</p>
              <p className="text-sm text-gray-500">Intervalo Auto-Check</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
